import z from 'zod';

type SamlMessageKey =
  | 'saml.provider_name_required'
  | 'saml.provider_name_too_long'
  | 'saml.email_domain_required'
  | 'saml.attr_email_required'
  | 'saml.attr_first_name_required'
  | 'saml.attr_last_name_required'
  | 'saml.attr_roles_required'
  | 'saml.metadata_required'
  | 'saml.metadata_url_invalid'
  | 'saml.metadata_exclusive';

// Cognito's own ProviderName field has a hard 32-character limit, enforced only downstream at the
// CreateIdentityProvider call behind the POST — portal-backend itself applies no length check.
// Exported so the page's own provider_name field validator can render this inline rather than
// relying on the form-level validator below, whose error string has no rendered slot.
export const COGNITO_PROVIDER_NAME_MAX_LENGTH = 32;

// http(s) only: new URL() also accepts ftp:, mailto:, javascript:, and a bare dotted scheme like
// "idp.example.com:8080/x" — none of which the backend's AnyHttpUrl response model or a real IdP
// metadata endpoint can be, so admitting them would defeat the point of validating client-side.
export function isValidUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Message lookup, satisfied by the page's `t` from `useTranslation('auth')`. Spelling the keys out
 *  keeps the schema testable without standing up i18n, and keeps `t`'s key checking. */
type Translate = (key: SamlMessageKey) => string;

/** What `metadata_file` holds in form state: `''` for none, a `File` for a newly picked upload, or
 *  the provider's existing metadata XML exactly as the GET returns it — Cognito's
 *  `ProviderDetails.MetadataFile`, which is document *content*, never a filename. Both non-empty
 *  shapes are directly postable, so the value that is validated is the value that is submitted. */
export type SamlMetadataFile = File | string;

export interface SamlProviderFormValues {
  provider_name: string;
  email_domain: string;
  metadata_url: string;
  metadata_file: SamlMetadataFile;
  attr_email: string;
  attr_first_name: string;
  attr_last_name: string;
  attr_roles: string;
}

/** Outside the page component so the two metadata rules can be tested directly — the backend
 *  enforces both, and rejects the both-at-once case with a bare, unactionable 400. */
export function buildSamlSchema(t: Translate) {
  return (
    z
      .object({
        provider_name: z
          .string()
          .min(1, t('saml.provider_name_required'))
          .max(COGNITO_PROVIDER_NAME_MAX_LENGTH, t('saml.provider_name_too_long')),
        email_domain: z.string().min(1, t('saml.email_domain_required')),
        attr_email: z.string().min(1, t('saml.attr_email_required')),
        attr_first_name: z.string().min(1, t('saml.attr_first_name_required')),
        attr_last_name: z.string().min(1, t('saml.attr_last_name_required')),
        attr_roles: z.string().min(1, t('saml.attr_roles_required')),
        metadata_url: z.string().optional(),
        metadata_file: z.union([z.string(), z.instanceof(File)]).optional(),
      })
      // Both rules report against metadata_url: it is the only one of the pair with an error slot
      // to render into, and a two-element path would read as a nested one.
      .refine(data => data.metadata_url || data.metadata_file, {
        message: t('saml.metadata_required'),
        path: ['metadata_url'],
      })
      .refine(data => !(data.metadata_url && data.metadata_file), {
        message: t('saml.metadata_exclusive'),
        path: ['metadata_url'],
      })
      // Empty is valid here — it's what a selected metadata file leaves behind — so this only
      // rejects a non-empty value that isn't a well-formed URL.
      .refine(data => !data.metadata_url || isValidUrl(data.metadata_url), {
        message: t('saml.metadata_url_invalid'),
        path: ['metadata_url'],
      })
  );
}
